import {
  useState,
  useRef,
  useCallback,
  useEffect,
  useLayoutEffect,
} from "react";
import { useMenu } from "./context/MenuContext";
import SecondaryPanel from "./parts/SecondaryPanel";
import MenuToggleButton from "./parts/MenuToggleButton";
import PrimaryFilterGroup from "./parts/PrimaryFilterGroup";
import MenuInfoBlock from "./parts/ui/MenuInfoBlock";
import SearchBlock from "./parts/SearchBlock";
import ViewToggleButton from "./parts/ViewToggleButton";
import SortButtons from "./parts/SortButtons";
import SortPanelLight from "./parts/SortPanelLight";
import MenuBottomLogo from "./parts/ui/MenuBottomLogo";
//import ActiveFiltersBlock from "./parts/ui/ActiveFiltersBlock";
//import FiltersDefBlock from "./parts/ui/FiltersDefBlock";
import { useDeviceDetection } from "../../hooks/useDeviceDetection";
import "./Menu.css";
import { Info/*, CirclePlus*/ } from "lucide-react";

const Menu = ({ total, visible }) => {
  //
  const {
    hierarchy,
    resetFilters,
    content,
    colorFiltre,
    deactivateChildren,
    onOpenPresentation,
  } = useMenu();

  const { isMobile } = useDeviceDetection();
  const [isOpen, setIsOpen] = useState(isMobile ? false : true);
  const [openParent, setOpenParent] = useState(null);
  const [infoAnchor, setInfoAnchor] = useState(null);
  const infoTimerRef = useRef(null);
  const [panelPos, setPanelPos] = useState({ left: 0, top: 0 });
  const menuRef = useRef(null);
  const keys = Object.keys(hierarchy || {});
  const primaryButtonRefs = useRef({});
  const triBtnRef = useRef(null);
  const [isSortPanelOpen, setIsSortPanelOpen] = useState(false);

  // callback appelé par PrimaryFilterGroup (bouton "i")
  const handleInfoClick = useCallback(
    (key, couleur, parentKey = null) => {
      const map = content?.def_typo || {};
      const txt =
        map[key] || map[key.split("_")[0]] || `Pas de définition pour "${key}"`;

      setInfoAnchor({
        key,
        parentKey,
        text: txt,
        color: couleur,
      });

      if (infoTimerRef.current) clearTimeout(infoTimerRef.current);

      infoTimerRef.current = setTimeout(() => {
        setInfoAnchor(null);
        infoTimerRef.current = null;
      }, 10000);
    },
    [content],
  );

  // Fonction utilitaire pour vérifier l'existence d'une définition
  const checkHasDefinition = useCallback(
    (key) => {
      return !!(content?.def_typo && content.def_typo[key]);
    },
    [content],
  );

  useEffect(() => {
    return () => {
      if (infoTimerRef.current) clearTimeout(infoTimerRef.current);
    };
  }, []);

  // fermer info, si fermeture menu
  useEffect(() => {
    if (!isOpen && infoAnchor) {
      setInfoAnchor(null);
      if (infoTimerRef.current) {
        clearTimeout(infoTimerRef.current);
        infoTimerRef.current = null;
      }
    }
  }, [isOpen, infoAnchor]);

  // ✅ fermeture/ouverture menu sur Scroll horizontal (desktop)
  useEffect(() => {
    const handleWheel = (e) => {
      // On ne réagit que si le scroll est horizontal
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
        if (e.deltaX > 30) setIsOpen(false); // vers la droite → fermer
        if (e.deltaX < -30) setIsOpen(true); // vers la gauche → ouvrir
      }
    };

    window.addEventListener("wheel", handleWheel, { passive: true });
    return () => window.removeEventListener("wheel", handleWheel);
  }, []);

  useLayoutEffect(() => {
    if (!openParent) return;
    const btn = primaryButtonRefs.current[openParent];
    const menu = menuRef.current;

    if (!btn || !menu) return;

    const btnRect = btn.getBoundingClientRect();
    const menuRect = menu.getBoundingClientRect();

    setPanelPos({
      left: menuRect.right,
      top: btnRect.top - 2,
    });
  }, [
    openParent,
    infoAnchor, // 👈 apparition/disparition du bloc info
    isOpen, // 👈 ouverture / fermeture du menu
    isSortPanelOpen,
  ]);

  /********** */
  // 1. Ajout d'une fonction de recalcul pour le resize
  const updatePanelPosition = useCallback(() => {
    if (isSortPanelOpen) {
      const menu = menuRef.current;

      if ( !menu) return;

      //const btnRect = btn.getBoundingClientRect();
      const menuRect = menu.getBoundingClientRect();
      //console.log("> menuRect.right", menuRect.right);
      setPanelPos({
        left: menuRect.right,
        top: 0, //btnRect.top - 2,
      });
    }

    if (!openParent || !isOpen) return;

    const btn = primaryButtonRefs.current[openParent];
    const menu = menuRef.current;

    if (btn && menu) {
      const btnRect = btn.getBoundingClientRect();
      const menuRect = menu.getBoundingClientRect();

      setPanelPos({
        left: menuRect.right, // Se cale précisément sur le bord droit du menu
        top: btnRect.top - 2,
      });
      /* console.log(
        "menuRect.right",
        menuRect.right,
        " btnRect.top",
        btnRect.top,
      );*/
    }
  }, [openParent, isOpen, isSortPanelOpen]);

  // 2. Écouteur pour le redimensionnement de la fenêtre
  useEffect(() => {
    window.addEventListener("resize", updatePanelPosition);
    return () => window.removeEventListener("resize", updatePanelPosition);
  }, [updatePanelPosition]);

  // 3. Mise à jour du positionnement lors des changements d'état internes
  useLayoutEffect(() => {
    updatePanelPosition();
  }, [
    openParent,
    infoAnchor,
    isOpen,
    isSortPanelOpen,
    updatePanelPosition, // Dépendance ajoutée pour réagir au resize via la fonction mémoïsée
  ]);

  /********** */

  /*
  // ✅ Swipe horizontal (mobile/tactile)
  useEffect(() => {
    let startX = 0;

    const handleTouchStart = (e) => {
      startX = e.touches[0].clientX;
    };

    const handleTouchEnd = (e) => {
      const endX = e.changedTouches[0].clientX;
      const deltaX = endX - startX;

      if (Math.abs(deltaX) > 50) { // seuil en px
        if (deltaX > 0) setIsOpen(true);   // swipe vers la droite → ouvrir
        else setIsOpen(false);             // swipe vers la gauche → fermer
      }
    };

    window.addEventListener("touchstart", handleTouchStart);
    window.addEventListener("touchend", handleTouchEnd);

    return () => {
      window.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("touchend", handleTouchEnd);
    };
  }, []);
*/
  /* --------------------------------------------------
     Render
  -------------------------------------------------- */
  return (
    <div ref={menuRef} className={`menu ${isOpen ? "open" : "closed"}`}>
      <div className="menu-inner">
        {/* Bouton toggle */}

        <MenuToggleButton isOpen={isOpen} onToggle={() => setIsOpen(!isOpen)} />

        {/* Infos compteur */}
        <MenuInfoBlock visible={visible} total={total} isOpen={isOpen} />

        {/* Boutons primaires */}
        <div className="menu-filters-block">
          {keys
            .filter((val) => hierarchy[val]?.primary)
            .sort((a, b) => {
              if (a === "Autre") return 1;
              if (b === "Autre") return -1;
              if (a === "Divers") return 1;
              if (b === "Divers") return -1;
              return a.localeCompare(b, "fr", { sensitivity: "base" });
            })
            .map((val) => (
              <PrimaryFilterGroup
                key={val}
                val={val}
                openParent={openParent}
                setOpenParent={setOpenParent}
                isOpen={isOpen}
                deactivateChildren={deactivateChildren}
                onInfoClick={handleInfoClick}
                infoAnchor={infoAnchor}
                registerRef={(el) => {
                  if (el) primaryButtonRefs.current[val] = el;
                }}
                hasDefinition={checkHasDefinition(val)}
              />
            ))}
        </div>

        {/* Liste des filtres actifs */}
        {/*isOpen && <ActiveFiltersBlock activeFilters={activeFilters} />*/}

        {/* Bloc secondaire */}
        {isOpen && openParent && (
          <SecondaryPanel
          //isOpen={isOpen && openParent}
            parent={openParent}
            childrenTags={hierarchy[openParent]?.children || []}
            onClose={() => setOpenParent(null)}
            left={panelPos.left}
            top={panelPos.top}
            onInfoClick={handleInfoClick}
            resetFilters={resetFilters}
            // Nouvelle prop : on passe la fonction pour que le panel vérifie ses enfants
            checkHasDefinition={checkHasDefinition}
          />
        )}

        {/* Zone recherche */}
        {isOpen && <SearchBlock />}

        {/* boutons tri / restore */}
        <SortButtons
          isOpen={isOpen}
          menuRef={menuRef}
          triBtnRef={triBtnRef}
          sortPanelOpen={isSortPanelOpen}
          setSortPanelOpen={setIsSortPanelOpen}
          triLight={true}
        />

        {/*isSortPanelOpen && (
          <SortPanel
            //menuRef={menuRef}
            left={panelPos.left}
            triBtnRef={triBtnRef}
            onClose={() => setIsSortPanelOpen(false)}
          />
        )*/}

        {isSortPanelOpen && (
          <SortPanelLight
            //isOpen={isSortPanelOpen}
            left={panelPos.left}
            triBtnRef={triBtnRef}
            onClose={() => setIsSortPanelOpen(false)}
          />
        )}

        <div className={`menu-nav-container ${isOpen ? "open" : "closed"}`}>
          <button
            className={`infos-btn ${isOpen ? "open" : "closed"}`}
            onClick={onOpenPresentation}
            title="infos"
            type="button"
          >
            <Info size={16} strokeWidth={2} />
            {isOpen && " infos"}
          </button>
          <ViewToggleButton isOpen={isOpen} />
        </div>

        {/* Logo en bas */}
        <MenuBottomLogo colorFiltre={colorFiltre} isFiltred={visible < total} />
      </div>
    </div>
  );
};

export default Menu;
